import EmailConfigurationFormComponent from '../components/emailConfiguration-form.component';
import { EmailConfigurationUpdate } from '../types/emailConfiguration.types';
import {
  useEmailConfigurationGet,
  useEmailConfigurationUpdate,
} from '../hooks/use-emailConfiguration.hook';
import { message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  extractCreateError,
  extractValidationError,
} from '../../../../../services/extract-api-error.service';
import { useEffect, useState, useCallback } from 'react';
import HeaderRow from '../../../../common/HeaderRow';
import { ValidationError } from '../../../../../types/api-response.types';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { DefaultValues } from 'react-hook-form';
import { WithSysAdminPermissionCheck } from '@components/auth/WithSysAdminPermissionCheck';
import PageTitle from '@components/common/PageTitle';
import { decrypt, isEncrypted } from '../../../../../utils/crypto.util';
import { getConfig } from '../../../../../config';

interface FormSubmitHandler {
  (data: EmailConfigurationUpdate, resetForm: () => void): void;
}

const EmailConfigurationUpdateContainer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [formDefaultValues, setFormDefaultValues] = useState<
    DefaultValues<EmailConfigurationUpdate>
  >({});

  // Move all hook calls before any conditional logic
  const { data, isLoading, error } = useEmailConfigurationGet(id ?? '');
  const { mutateAsync: updateEmailConfiguration, isPending } = useEmailConfigurationUpdate();

  const extractFormDefaultValues = useCallback(() => {
    if (!data?.data) return;
    const { data: emailConfigurationData } = data;
    const { flightEncryptionKey } = getConfig();

    let decryptedPassword: string | undefined = undefined;
    if (emailConfigurationData.password) {
      try {
        if (isEncrypted(emailConfigurationData.password)) {
          decryptedPassword = decrypt(emailConfigurationData.password, flightEncryptionKey);
        } else {
          decryptedPassword = emailConfigurationData.password;
        }
      } catch (error) {
        console.error('Failed to decrypt password:', error);
        // If decryption fails, leave it undefined or use empty string
        decryptedPassword = undefined;
      }
    }

    setFormDefaultValues({
      email: emailConfigurationData.email ?? undefined,
      host: emailConfigurationData.host ?? undefined,
      port: emailConfigurationData.port ?? undefined,
      password: decryptedPassword,
      isSslTls: emailConfigurationData.isSslTls ?? undefined,
    });
  }, [data]);

  useEffect(() => {
    let isMounted = true; // to prevent memory leaks
    extractFormDefaultValues();
    return () => {
      isMounted = false;
    };
  }, [extractFormDefaultValues]);

  const onFormSubmit: FormSubmitHandler = useCallback(
    async (data, resetForm) => {
      if (!id) return;
      // Reset validation errors on new submission
      setValidationErrors([]);
      try {
        await updateEmailConfiguration({ id, data });
        message.success(t('crud.update.success', { entity: t('emailConfiguration.title') }));
        navigate(-1);
      } catch (error: any) {
        const errorFields = extractValidationError(error);
        setValidationErrors(errorFields);
        const errorMessage = extractCreateError(
          error,
          t('crud.update.error', { entity: t('emailConfiguration.title') })
        );
        message.error(errorMessage);
      }
    },
    [id, updateEmailConfiguration, t, navigate]
  );

  if (!id) {
    return (
      <ErrorFallback
        details={t('crud.details.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );
  }

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.update.title', { entity: t('emailConfiguration.title') })} />
        <HeaderRow
          title={t('crud.update.title', {
            entity: t('emailConfiguration.title'),
          })}
          showBackButton={true}
        />
        <ErrorFallback
          details={t('crud.details.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={t('crud.update.title', { entity: t('emailConfiguration.title') })} />
      <HeaderRow
        title={t('crud.update.title', { entity: t('emailConfiguration.title') })}
        showBackButton={true}
        isSysAdmin={true}
      />
      <div className="row mt-3">
        <EmailConfigurationFormComponent
          onFormSubmit={onFormSubmit}
          isLoading={isPending || isLoading}
          defaultValues={formDefaultValues}
          columns={3}
          validationErrors={validationErrors}
        />
      </div>
    </>
  );
};

export default WithSysAdminPermissionCheck(
  EmailConfigurationUpdateContainer,
  'emailConfiguration.update'
);
